/**
 * The doctor a strategy is for (Aziz's team, 2026-09-28): who they are, their licensed
 * title and clinic fields, how they want their footage handled, and the team's profile of
 * them (licence, style, rules). The profile belongs to the doctor, so it follows them into
 * every strategy. The planners pick the doctor and keep the profile current.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Stethoscope } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { qk, fetchStrategyDoctor, pickStrategyDoctor, saveDoctorProfile, type Strategy } from '@/lib/queries';

type Talent = { id: string; name: string };

export default function DoctorCard({ s, talents, canEdit }: { s: Strategy; talents: Talent[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['strategy-doctor', s.id];
  const doc = useQuery({ queryKey: key, queryFn: () => fetchStrategyDoctor(s.id) });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const refresh = (d: unknown) => { qc.setQueryData(key, d); qc.invalidateQueries({ queryKey: qk.strategy(s.id) }); };
  const pick = useMutation({ mutationFn: (tid: string) => pickStrategyDoctor(s.id, tid || null), onSuccess: (d) => { refresh(d); toast.success(d ? `This strategy is for ${d.name}` : 'No doctor on this strategy'); },
    onError: () => toast.error('That did not go through') });
  const save = useMutation({ mutationFn: () => saveDoctorProfile(s.id, draft), onSuccess: (d) => { refresh(d); setEditing(false); toast.success('Profile saved on the doctor'); },
    onError: () => toast.error('That did not go through') });
  const d = doc.data;
  if (!d && !canEdit) return null;

  return (
    <Card className="mb-5" data-testid="doctor-card">
      <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide text-muted mb-3">
        <Stethoscope size={12} /> The doctor
        {canEdit && talents.length > 0 && (
          <select value={d?.talent_id ?? ''} onChange={(e) => pick.mutate(e.target.value)} aria-label="The doctor this strategy is for" data-testid="doctor-pick"
            className="ml-auto normal-case tracking-normal text-xs h-8 px-2 rounded-lg bg-bg border border-slate-800 text-muted-strong">
            <option value="">No doctor</option>
            {talents.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>
      {!d ? <p className="text-sm text-muted">Pick the doctor this strategy is for, and their profile shows here for the whole team.</p> : (
        <div className="flex flex-col sm:flex-row gap-4">
          {d.photo_url
            ? <img src={d.photo_url} alt="" className="w-20 h-24 rounded-lg object-cover border border-slate-800 shrink-0" />
            : <div className="w-20 h-24 rounded-lg bg-slate-800 flex items-center justify-center text-2xl text-muted-strong shrink-0">{d.name.replace(/^Dr\.?\s*/i, '').slice(0, 1)}</div>}
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-muted-strong">{d.name}</div>
            {d.speciality && <div className="text-xs text-muted">{d.speciality}</div>}
            {d.fields.length > 0 && (
              <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2 text-xs">
                {d.fields.map((f) => <div key={f.label} className="flex gap-2 min-w-0"><dt className="text-muted shrink-0">{f.label}</dt><dd dir="auto" className="text-muted-strong [overflow-wrap:anywhere]">{f.value}</dd></div>)}
              </dl>
            )}
            {d.production_notes && (
              <div className="mt-3 text-xs"><div className="text-muted mb-0.5">How they want their footage handled</div>
                <p dir="auto" className={`text-muted-strong whitespace-pre-wrap ${BIDI_CLS}`}>{bidi(d.production_notes)}</p></div>
            )}
            <div className="mt-3">
              <div className="flex items-center gap-2 text-xs text-muted mb-1">Profile: licence, style, rules
                {d.can_edit && !editing && <button type="button" onClick={() => { setDraft(d.profile ?? ''); setEditing(true); }} className="inline-flex items-center gap-1 text-accent-400 hover:underline" data-testid="doctor-profile-edit"><Pencil size={11} /> Edit</button>}
              </div>
              {editing ? (
                <div className="space-y-2">
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={10} maxLength={8000} dir="auto" data-testid="doctor-profile-text"
                    placeholder="Licence and title, how they speak on camera, what they will and will not say, what works for them, rules to respect."
                    className="w-full px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                    <Button size="sm" loading={save.isPending} onClick={() => save.mutate()} data-testid="doctor-profile-save">Save on the doctor</Button>
                  </div>
                </div>
              ) : d.profile
                ? <p dir="auto" className={`text-sm text-muted-strong whitespace-pre-wrap ${BIDI_CLS}`} data-testid="doctor-profile">{bidi(d.profile)}</p>
                : <p className="text-sm text-muted">No profile yet.</p>}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
